import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  TemplateRef,
  Output,
  EventEmitter
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { PopNotificationsService } from "@core/services/pop-notifications.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { NgbModal, NgbModalRef, NgbOffcanvas, NgbOffcanvasRef } from "@ng-bootstrap/ng-bootstrap";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import {
  AbuseReportReason,
  ABUSE_REPORT_REASON_LABELS,
  ABUSE_REPORT_CONTENT_TYPES
} from "@shared/interfaces/abuse-report.interface";
import { AbuseReportApiService } from "@shared/services/abuse-report-api.service";

export interface ReportAbuseModalConfig {
  contentType: "post" | "topic" | "message" | "comment";
  contentId: string | number;
  contentTitle?: string;
  authorName?: string;
  onSuccess?: () => void;
}

@Component({
  selector: "astrobin-report-abuse-modal",
  templateUrl: "./report-abuse-modal.component.html",
  styleUrls: ["./report-abuse-modal.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportAbuseModalComponent implements OnInit {
  @ViewChild("modalTemplate", { static: true }) modalTemplate!: TemplateRef<any>;
  @ViewChild("offcanvasTemplate", { static: true }) offcanvasTemplate!: TemplateRef<any>;
  @Output() reportSubmitted = new EventEmitter<void>();

  form = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  submitting = false;
  config: ReportAbuseModalConfig | null = null;
  additionalInfoMaxLength = 500;

  private modalRef: NgbModalRef | null = null;
  private offcanvasRef: NgbOffcanvasRef | null = null;

  constructor(
    private readonly modalService: NgbModal,
    private readonly offcanvasService: NgbOffcanvas,
    private readonly abuseReportApi: AbuseReportApiService,
    private readonly popNotificationsService: PopNotificationsService,
    private readonly translateService: TranslateService,
    private readonly windowRef: WindowRefService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeFormFields();
  }

  private initializeFormFields(): void {
    const additionalInfoField = {
      key: "additionalInformation",
      type: "textarea",
      templateOptions: {
        label: this.translateService.instant("Additional information"),
        placeholder: this.translateService.instant(
          "Please provide any additional details that may help moderators review this report"
        ),
        rows: 4,
        maxLength: this.additionalInfoMaxLength,
        description: this.getCharacterCountDescription()
      },
      hooks: {
        onInit: field => {
          // Subscribe to value changes to update the description
          field.formControl.valueChanges.subscribe(() => {
            field.templateOptions.description = this.getCharacterCountDescription();
            this.changeDetectorRef.markForCheck();
          });
        }
      }
    };

    this.fields = [
      {
        key: "reason",
        type: "ng-select",
        templateOptions: {
          label: this.translateService.instant("Reason for reporting"),
          required: true,
          options: Object.values(AbuseReportReason).map(reason => ({
            value: reason,
            label: this.translateService.instant(ABUSE_REPORT_REASON_LABELS[reason])
          })),
          placeholder: this.translateService.instant("Select a reason")
        }
      },
      additionalInfoField
    ];
  }

  private getCharacterCountDescription(): string {
    const currentLength = this.model?.additionalInformation?.length || 0;
    const remaining = this.additionalInfoMaxLength - currentLength;
    return `${remaining} ${this.translateService.instant("characters remaining")}`;
  }

  open(config: ReportAbuseModalConfig): void {
    this.config = config;
    this.model = {
      reason: AbuseReportReason.SPAM,
      additionalInformation: ""
    };
    this.submitting = false;
    this.initializeFormFields();

    // Use offcanvas on mobile, modal on desktop
    if (this.windowRef.nativeWindow.innerWidth <= 768) {
      this.offcanvasRef = this.offcanvasService.open(this.offcanvasTemplate, {
        position: "bottom",
        panelClass: "report-abuse-offcanvas"
      });
    } else {
      this.modalRef = this.modalService.open(this.modalTemplate, {
        centered: true,
        size: "md"
      });
    }
  }

  close(): void {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }
    if (this.offcanvasRef) {
      this.offcanvasRef.close();
      this.offcanvasRef = null;
    }
  }

  get contentTypeLabel(): string {
    if (!this.config) {
      return "";
    }

    const labels = {
      post: this.translateService.instant("post"),
      topic: this.translateService.instant("topic"),
      message: this.translateService.instant("message"),
      comment: this.translateService.instant("comment")
    };

    return labels[this.config.contentType] || this.config.contentType;
  }

  get modalTitle(): string {
    if (!this.config) {
      return this.translateService.instant("Report content");
    }

    if (this.config.authorName) {
      return this.translateService.instant("Report {{ type }} by {{ author }}", {
        type: this.contentTypeLabel,
        author: this.config.authorName
      });
    }

    return this.translateService.instant("Report {{ type }}", {
      type: this.contentTypeLabel
    });
  }

  get remainingChars(): number {
    const value = this.model?.additionalInformation || "";
    return this.additionalInfoMaxLength - value.length;
  }

  async submit(): Promise<void> {
    if (!this.form.valid || !this.config || this.submitting) {
      return;
    }

    this.submitting = true;
    const formValue = this.model;

    try {
      // Use universal endpoint for all content types
      const contentTypeStr = ABUSE_REPORT_CONTENT_TYPES[this.config.contentType];
      await this.abuseReportApi
        .submitReport({
          content_type_str: contentTypeStr,
          object_id: String(this.config.contentId),
          reason: formValue.reason,
          additional_information: formValue.additionalInformation || ""
        })
        .toPromise();

      this.popNotificationsService.success(
        this.translateService.instant("Thank you for your report. A moderator will review it soon.")
      );

      // Emit event and call success callback
      this.reportSubmitted.emit();
      if (this.config?.onSuccess) {
        this.config.onSuccess();
      }

      this.close();
    } catch (error: any) {
      this.handleError(error);
    } finally {
      this.submitting = false;
    }
  }

  private handleError(error: any): void {
    if (error.status === 400) {
      const errorMessage = error.error?.detail || error.error || "";

      if (errorMessage.includes("already reported")) {
        this.popNotificationsService.warning(this.translateService.instant("You have already reported this content"));
      } else if (errorMessage.includes("quota")) {
        this.popNotificationsService.warning(
          this.translateService.instant("Daily report limit reached. Try again tomorrow.")
        );
      } else if (errorMessage.includes("approved by a moderator")) {
        this.popNotificationsService.info(
          this.translateService.instant("This content was already reviewed and approved")
        );
      } else {
        this.popNotificationsService.error(this.translateService.instant("Unable to submit report. Please try again."));
      }
    } else if (error.status === 401) {
      this.popNotificationsService.error(this.translateService.instant("Please log in to report content"));
    } else {
      this.popNotificationsService.error(this.translateService.instant("Unable to submit report. Please try again."));
    }
  }
}
